import { useState } from 'react';
import { UserButton, useOrganization } from '@clerk/clerk-react';
import WorkspaceOrganizationSwitcher from './WorkspaceOrganizationSwitcher';
import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import OrganizationGate from './OrganizationGate';
import NotificationCenter from './NotificationCenter';
import { userButtonAppearance } from '../theme/clerkAppearance';

const NAV_ITEMS = [
    {
        label: 'Investigations',
        path: '/investigations',
        disabled: false,
        icon: <InvestigationsIcon />
    },
    {
        label: 'Integrations',
        path: '/integrations',
        disabled: false,
        icon: <SettingsIcon />
    },
    {
        label: 'Notifications',
        path: '/notifications',
        disabled: false,
        icon: <NotificationsNavIcon />
    },
    {
        label: 'Remediation',
        path: '/remediation',
        disabled: false,
        icon: <RemediationNavIcon />
    },
    {
        label: 'Forecast',
        path: '/forecast',
        disabled: false,
        icon: <ForecastNavIcon />
    }
];

export default function Layout({ children }: { children: ReactNode }) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { organization } = useOrganization();

    const closeSidebar = () => setSidebarOpen(false);

    return (
        <div className="layout">
            {sidebarOpen && (
                <div className="sidebar-overlay" onClick={closeSidebar} />
            )}

            <aside
                className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}`}
                style={{ display: 'flex', flexDirection: 'column' }}
            >
                <div
                    className="sidebar-section"
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: '16px'
                    }}
                >
                    <div
                        className="sidebar-brand"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '12px 10px 20px 8px'
                        }}
                    >
                        <img
                            alt="SelfHeal"
                            width="30"
                            height="30"
                            style={{
                                width: '30px',
                                height: '30px',
                                objectFit: 'contain'
                            }}
                            src="/logo.webp"
                        />
                        <span
                            style={{
                                fontFamily: 'var(--font-display)',
                                fontSize: '15px',
                                fontWeight: 600,
                                color: 'var(--text-h)',
                                letterSpacing: '-0.01em'
                            }}
                        >
                            Healops
                        </span>
                    </div>

                    <nav className="sidebar-nav" style={{ flexGrow: 1 }}>
                        {NAV_ITEMS.map(({ label, path, disabled, icon }) =>
                            disabled ? (
                                <span
                                    key={path}
                                    className="sidebar-link sidebar-link-disabled"
                                >
                                    {icon}
                                    {label}
                                </span>
                            ) : (
                                <NavLink
                                    key={path}
                                    to={path}
                                    className={({ isActive }) =>
                                        `sidebar-link${isActive ? ' active' : ''}`
                                    }
                                    onClick={closeSidebar}
                                >
                                    {icon}
                                    {label}
                                </NavLink>
                            )
                        )}
                    </nav>
                </div>

                {/* Workspace Organization Switcher at the bottom */}
                <div
                    className="workspace-selector-container"
                    style={{
                        padding: '16px 12px',
                        borderTop: '1px solid var(--border)'
                    }}
                >
                    <span
                        style={{
                            fontSize: '10px',
                            color: 'var(--text-muted)',
                            fontWeight: 600,
                            display: 'block',
                            marginBottom: '6px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                        }}
                    >
                        Active Workspace
                    </span>
                    <WorkspaceOrganizationSwitcher variant="sidebar" />
                </div>
            </aside>

            <div className="layout-right">
                <header
                    className="header"
                    style={{
                        backdropFilter: 'blur(16px)',
                        background: 'rgba(8, 8, 10, 0.85)',
                        borderBottom: '1px solid var(--border)'
                    }}
                >
                    <button
                        className="menu-btn"
                        aria-label="Toggle navigation"
                        onClick={() => setSidebarOpen((o) => !o)}
                    >
                        <MenuIcon />
                    </button>
                    <div className="header-right">
                        {/* Sits above OrganizationGate, so key it by org to drop
                            the previous workspace's notifications on a switch. */}
                        <NotificationCenter key={organization?.id ?? 'none'} />
                        <UserButton
                            afterSignOutUrl="/"
                            appearance={userButtonAppearance}
                        />
                    </div>
                </header>

                <div className="layout-body">
                    <main className="main-content">
                        <OrganizationGate>{children}</OrganizationGate>
                    </main>
                </div>
            </div>
        </div>
    );
}

function MenuIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
            <rect y="3" width="18" height="1.5" rx="0.75" fill="currentColor" />
            <rect
                y="8.25"
                width="18"
                height="1.5"
                rx="0.75"
                fill="currentColor"
            />
            <rect
                y="13.5"
                width="18"
                height="1.5"
                rx="0.75"
                fill="currentColor"
            />
        </svg>
    );
}

function InvestigationsIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle
                cx="6.5"
                cy="6.5"
                r="4"
                stroke="currentColor"
                strokeWidth="1.5"
            />
            <path
                d="M10.5 10.5L13.5 13.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

function SettingsIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <circle
                cx="8"
                cy="8"
                r="2.5"
                stroke="currentColor"
                strokeWidth="1.5"
            />
            <path
                d="M8 1.5V3M8 13v1.5M1.5 8H3M13 8h1.5M3.4 3.4l1.1 1.1M11.5 11.5l1.1 1.1M3.4 12.6l1.1-1.1M11.5 4.5l1.1-1.1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
        </svg>
    );
}

function NotificationsNavIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
                d="M8 2a4.5 4.5 0 0 0-4.5 4.5v2L2 9.75V10.5h12v-.75L12.5 8.5V6.5A4.5 4.5 0 0 0 8 2Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
            <path
                d="M6.5 11a1.5 1.5 0 0 0 3 0"
                stroke="currentColor"
                strokeWidth="1.4"
            />
        </svg>
    );
}

function ForecastNavIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
                d="M2 12.5 6 8l3 2.5L14 4.5M10.5 4.5H14V8"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function RemediationNavIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
                d="M9.5 2.5a3 3 0 0 0-1.2 4.9L3 12.7l1.3 1.3 5.3-5.3a3 3 0 0 0 3.9-3.6l-1.9 1.9-1.4-.4-.4-1.4 1.9-1.9a3 3 0 0 0-2.1.2Z"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
        </svg>
    );
}
